<template>
  <div class="name-page">
    <!-- Header Section -->
    <AppHeader 
      header-text="Drivers in your state can save up to $600/year"
      button-text="FREE CALL"
    />

    <!-- Progress Bar -->
    <div class="progress-container">
      <div class="progress-bar">
        <div class="progress-fill"></div>
      </div>
    </div>

    <!-- Navigation -->
    <div class="navigation">
      <div class="container">
        <button class="back-btn" @click="goBack">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Previous Question
        </button>
      </div>
    </div>

    <!-- Name Section -->
    <section class="name-section">
      <div class="container">
        <div class="name-content">
          <div v-if="isAddingDriver" class="driver-indicator">
            2ND DRIVER
          </div>
          <PageTitle title="Name" />
          
          <div class="name-form">
            <!-- Relationship Dropdown for additional drivers -->
            <div v-if="isAddingDriver" class="input-group">
              <select
                v-model="relationship"
                class="name-input"
                :class="{ 'error': showRelationshipError }"
                @change="clearRelationshipError"
              >
                <option value="">Relationship to you</option>
                <option value="Child">Child</option>
                <option value="Domestic Partner">Domestic Partner</option>
                <option value="Grandchild">Grandchild</option>
                <option value="Grandparent">Grandparent</option>
                <option value="Other">Other</option>
                <option value="Parent">Parent</option>
                <option value="Self">Self</option>
                <option value="Sibling">Sibling</option>
                <option value="Spouse">Spouse</option>
              </select>
              <div v-if="showRelationshipError" class="error-message">Required</div>
            </div>

            <!-- First Name Input -->
            <div class="input-group">
              <input
                type="text"
                v-model="firstName"
                placeholder="Legal First Name"
                class="name-input"
                :class="{ 'error': showFirstNameError }"
                @input="clearFirstNameError"
              />
              <div v-if="showFirstNameError" class="error-message">Required</div>
            </div>

            <!-- Last Name Input -->
            <div class="input-group">
              <input
                type="text"
                v-model="lastName"
                placeholder="Legal Last Name"
                class="name-input"
                :class="{ 'error': showLastNameError }"
                @input="clearLastNameError"
              />
              <div v-if="showLastNameError" class="error-message">Required</div>
            </div>

            <!-- Continue Button -->
            <div class="continue-section">
              <button 
                class="continue-btn"
                @click="continueToNext"
              >
                CONTINUE
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer Links -->
    <AppFooter />
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import PageTitle from '@/components/PageTitle.vue'
import { submitDataAndLog } from '@/utils/localStorage'

export default {
  name: 'NamePage',
  components: {
    AppHeader,
    AppFooter,
    PageTitle
  },
  data() {
    return {
      userQuoteData: null,
      firstName: '',
      lastName: '',
      relationship: '',
      showFirstNameError: false,
      showLastNameError: false,
      showRelationshipError: false
    }
  },
  computed: {
    isAddingDriver() {
      return this.userQuoteData && this.userQuoteData.adding_driver
    }
  },
  mounted() {
    this.loadUserData()
  },
  methods: {
    loadUserData() {
      const storedData = localStorage.getItem('quoteData')
      if (storedData) {
        try {
          this.userQuoteData = JSON.parse(storedData)
          // 检查是否有选择的事故信息
          if (!this.userQuoteData.incidents) {
            // 如果没有事故信息，跳转回incidents页面
            this.$router.push('/incidents')
            return
          }
          // 检查是否是添加驾驶员模式
          if (this.userQuoteData.adding_driver && this.userQuoteData.temp_driver) {
            // 添加驾驶员模式：加载临时驾驶员数据
            this.firstName = this.userQuoteData.temp_driver.first_name || ''
            this.lastName = this.userQuoteData.temp_driver.last_name || ''
            this.relationship = this.userQuoteData.temp_driver.relationship || ''
          } else {
            // 主驾驶员模式：加载主驾驶员数据
            this.firstName = this.userQuoteData.first_name || ''
            this.lastName = this.userQuoteData.last_name || ''
          }
        } catch (error) {
          console.error('Error parsing stored data:', error)
          this.$router.push('/')
        }
      } else {
        // 如果没有数据，跳转回首页
        this.$router.push('/')
      }
    },
    
    saveUserData() {
      localStorage.setItem('quoteData', JSON.stringify(this.userQuoteData))
    },
    
    clearFirstNameError() {
      if (this.firstName && this.firstName.trim().length > 0) {
        this.showFirstNameError = false
      }
    },
    
    clearLastNameError() {
      if (this.lastName && this.lastName.trim().length > 0) {
        this.showLastNameError = false
      }
    },
    clearRelationshipError() {
      if (this.relationship && this.relationship !== '') {
        this.showRelationshipError = false
      }
    },
    
    async continueToNext() {
      console.log('CONTINUE button clicked in Name page')
      
      // 验证表单
      let hasErrors = false
      
      // 检查First Name
      if (!this.firstName || this.firstName.trim().length === 0) {
        this.showFirstNameError = true
        hasErrors = true
      } else {
        this.showFirstNameError = false
      }
      
      // 检查Last Name
      if (!this.lastName || this.lastName.trim().length === 0) {
        this.showLastNameError = true
        hasErrors = true
      } else {
        this.showLastNameError = false
      }
      
      // 如果是添加驾驶员模式，检查关系
      if (this.isAddingDriver) {
        if (!this.relationship || this.relationship === '') {
          this.showRelationshipError = true
          hasErrors = true
        } else {
          this.showRelationshipError = false
        }
      }
      
      // 如果有错误，不继续
      if (hasErrors) {
        console.log('Name validation failed - showing error messages')
        return
      }
      
      // 更新用户数据
      if (this.isAddingDriver) {
        // 添加驾驶员模式：完成临时驾驶员数据并保存到other_drivers数组
        this.userQuoteData.temp_driver.first_name = this.firstName.trim()
        this.userQuoteData.temp_driver.last_name = this.lastName.trim()
        this.userQuoteData.temp_driver.relationship = this.relationship
        
        console.log('Additional driver name and relationship saved:', {
          firstName: this.firstName.trim(),
          lastName: this.lastName.trim(),
          relationship: this.relationship
        })
        
        // 确保other_drivers数组存在
        if (!this.userQuoteData.other_drivers) {
          this.userQuoteData.other_drivers = []
        }
        
        // 将完整的驾驶员信息添加到other_drivers数组
        this.userQuoteData.other_drivers.push({
          first_name: this.userQuoteData.temp_driver.first_name,
          last_name: this.userQuoteData.temp_driver.last_name,
          relationship: this.userQuoteData.temp_driver.relationship,
          gender: this.userQuoteData.temp_driver.gender,
          marital_status: this.userQuoteData.temp_driver.marital_status,
          birthday: this.userQuoteData.temp_driver.birthday,
          birth_month: this.userQuoteData.temp_driver.birth_month,
          birth_day: this.userQuoteData.temp_driver.birth_day,
          birth_year: this.userQuoteData.temp_driver.birth_year
        })
        
        // 清理临时数据
        delete this.userQuoteData.temp_driver
        this.userQuoteData.adding_driver = false
        
        console.log('Added driver to other_drivers array:', this.userQuoteData.other_drivers)
        
        // 提交数据并记录页面提交事件
        await submitDataAndLog('name')
        
        // 跳转回AnotherDriver页面继续添加或完成
        this.$router.push('/another-driver')
      } else {
        // 主驾驶员模式：更新主驾驶员数据
        this.userQuoteData.first_name = this.firstName.trim()
        this.userQuoteData.last_name = this.lastName.trim()
        
        console.log('Primary driver name saved:', {
          firstName: this.firstName.trim(),
          lastName: this.lastName.trim()
        })
        
        // 提交数据并记录页面提交事件
        await submitDataAndLog('name')
        
        // 跳转到Another Driver页面
        this.$router.push('/another-driver')
      }
      
      this.saveUserData()
    },
    
    goBack() {
      this.$router.push('/incidents')
    }
  }
}
</script>

<style scoped>
/* Page Layout */
.name-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}


/* Navigation */
.navigation {
  padding: 1rem 0;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: #999;
  font-size: 0.9rem;
  cursor: pointer;
  transition: color 0.3s ease;
}

.back-btn:hover {
  color: #666;
}

/* Name Section */
.name-section {
  padding: 2rem 0 4rem;
  background: #fff;
  flex: 1;
}

.name-content {
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
  padding: 0 2rem;
}

.driver-indicator {
  color: #28a745;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.name-form {
  margin-top: 2rem;
}

.input-group {
  margin-bottom: 2rem;
}

.name-input {
  width: 100%;
  padding: 1.2rem 1rem;
  border: none;
  border-bottom: 2px solid #e9ecef;
  background: transparent;
  font-size: 1.1rem;
  color: #333;
  transition: border-color 0.3s ease;
  outline: none;
}

.name-input::placeholder {
  color: #999;
  font-weight: 400;
}

.name-input:focus {
  border-bottom-color: #007bff;
}

.name-input:not(:placeholder-shown) {
  border-bottom-color: #28a745;
}

.name-input.error {
  border-bottom-color: #dc3545;
}

.error-message {
  color: #dc3545;
  font-size: 0.8rem;
  margin-top: 0.5rem;
  text-align: left;
}

/* Continue Section */
.continue-section {
  margin-top: 3rem;
  padding-top: 2rem;
}

.continue-btn {
  padding: 1rem 3rem;
  border: 2px solid #007bff;
  border-radius: 8px;
  background: #fff;
  color: #007bff;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 200px;
}

.continue-btn:hover:not(:disabled) {
  background: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,123,255,0.15);
}

.continue-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Responsive Design */
@media (max-width: 768px) {
  .name-content {
    padding: 0 1rem;
  }

  .name-input {
    padding: 1rem 0.5rem;
    font-size: 1rem;
  }

  .continue-btn {
    width: 100%;
    max-width: 300px;
  }
}

@media (max-width: 480px) {
  .name-content {
    padding: 0 0.5rem;
  }

  .name-input {
    padding: 0.8rem 0;
    font-size: 0.9rem;
  }

  .continue-btn {
    padding: 0.8rem 2rem;
    font-size: 1rem;
  }

  .input-group {
    margin-bottom: 1.5rem;
  }
}
</style>
